<template>
  <div>

    <!-- ═══════════════════════════════════════════════════════════
         SHARED FILTER BAR — applies to both stats & table
    ═══════════════════════════════════════════════════════════ -->
    <b-card class="filter-bar-card mb-2">
      <b-card-text>

        <b-row class="align-items-end">
          <b-col
            md="4"
            cols="12"
          >
            <b-form-group
              :label="$t('common.from-date') || 'من تاريخ'"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.from_date"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="4"
            cols="12"
          >
            <b-form-group
              :label="$t('common.to-date') || 'الى تاريخ'"
              class="mb-md-0"
            >
              <datepicker
                v-model="filters.to_date"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="4"
            cols="12"
            class="d-flex align-items-end"
          >
            <div
              class="mb-2 d-flex"
              style="gap: 0.5rem"
            >
              <b-button
                variant="primary"
                :disabled="isLoading"
                @click="applyFilters"
              >
                <b-spinner
                  v-if="isLoading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-25"
                />
                {{ $t('common.search') || 'بحث' }}
              </b-button>
              <b-button
                variant="outline-secondary"
                :disabled="isLoading"
                @click="resetFilters"
              >
                <feather-icon
                  icon="XIcon"
                  size="14"
                  class="mr-25"
                />
                {{ $t('common.reset') || 'إعادة تعيين' }}
              </b-button>
            </div>
          </b-col>
        </b-row>

        <!-- Active date range pill -->
        <div
          v-if="activeDateLabel"
          class="active-filter-pill mt-2"
        >
          <feather-icon
            icon="CalendarIcon"
            size="12"
            class="mr-25"
          />
          {{ activeDateLabel }}
        </div>
      </b-card-text>
    </b-card>

    <!-- ═══════════════════════════════════════════════════════════
         STATISTICS SECTION - Surgery Categories
    ═══════════════════════════════════════════════════════════ -->
    <b-card class="stats-card mb-2">
      <b-card-text>
        <!-- Stats Header -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center">
            <div class="stats-icon-wrap mr-2">
              <feather-icon
                icon="PieChartIcon"
                size="20"
                class="stats-icon"
              />
            </div>
            <div>
              <h4 class="mb-0 stats-title">
                {{ $t('surgery.categories-statistics') || 'إحصائيات التصنيفات' }}
              </h4>

            </div>
          </div>
          <b-button
            variant="outline-primary"
            size="sm"
            class="refresh-btn"
            :disabled="loadingCategories"
            @click="fetchCategoryStats"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              :class="{ 'spin-icon': loadingCategories }"
            />
          </b-button>
        </div>

        <!-- Global Totals Strip -->
        <div
          v-if="!loadingCategories && categoryStats.length"
          class="totals-strip mb-3"
        >
          <div class="totals-strip-inner">
            <div class="total-item">
              <feather-icon
                icon="LayersIcon"
                size="16"
                class="mr-50 text-primary"
              />
              <span class="total-label">{{ $t('surgery.total-categories') || 'التصنيفات' }}:</span>
              <strong class="total-value ml-50">{{ categoryStats.length }}</strong>
            </div>
            <div class="total-divider" />
            <div class="total-item">
              <feather-icon
                icon="ActivityIcon"
                size="16"
                class="mr-50 text-info"
              />
              <span class="total-label">{{ $t('surgery.total-operations') || 'العمليات' }}:</span>
              <strong class="total-value ml-50">{{ grandTotalOperations }}</strong>
            </div>
            <div class="total-divider" />
            <div class="total-item">
              <feather-icon
                icon="DollarSignIcon"
                size="16"
                class="mr-50 text-success"
              />
              <span class="total-label">{{ $t('common.total-revenue') || 'إجمالي الإيراد' }}:</span>
              <strong class="total-value ml-50 text-success">{{ formatCurrency(grandTotalRevenue) }}</strong>
            </div>
            <div class="total-divider" />
            <!-- <div class="total-item">
              <feather-icon
                icon="CheckCircleIcon"
                size="16"
                class="mr-50 text-warning"
              />
              <span class="total-label">{{ $t('surgery.total-collected') || 'المحصّل' }}:</span>
              <strong class="total-value ml-50">{{ grandTotalCollected }}</strong>
            </div> -->
          </div>
        </div>

        <!-- Loading Skeleton -->
        <div
          v-if="loadingCategories"
          class="stats-skeleton-grid"
        >
          <div
            v-for="i in 4"
            :key="i"
            class="skeleton-card"
          >
            <div class="skeleton-header" />
            <div
              class="skeleton-bar"
              style="width: 70%"
            />
            <div
              class="skeleton-bar"
              style="width: 45%"
            />
            <div
              class="skeleton-bar"
              style="width: 55%"
            />
          </div>
        </div>

        <!-- Category Cards Grid -->
        <div
          v-else-if="categoryStats.length"
          class="categories-grid"
        >
          <div
            v-for="(cat, index) in categoryStats"
            :key="cat.id"
            class="category-card"
            :class="{ 'category-card--active': selectedCategoryId === cat.id }"
            :style="{ '--card-accent': getCategoryColor(index) }"
            @click="handleCategoryClick(cat)"
          >
            <div class="card-top-bar" />
            <div class="category-card-header">
              <div class="category-rank">
                {{ index + 1 }}
              </div>
              <div class="category-name-wrap">
                <h6 class="category-name mb-0">
                  {{ cat.category_name }}
                </h6>
              </div>
              <!-- <div class="category-share-badge">
                {{ getRevenueShare(cat.total_revenue) }}%
              </div> -->
            </div>

            <div class="category-stats">
              <div class="stat-row">
                <div class="stat-row-label">
                  <feather-icon
                    icon="ActivityIcon"
                    size="13"
                    class="mr-50"
                  />
                  {{ $t('surgery.total-operations') || 'عدد العمليات' }}
                </div>
                <b-badge
                  variant="primary"
                  pill
                  class="stat-badge"
                >
                  {{ cat.total_operations }}
                </b-badge>
              </div>
              <div class="stat-row">
                <div class="stat-row-label">
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="13"
                    class="mr-50"
                  />
                  {{ $t('common.total-revenue') || 'إجمالي الإيراد' }}
                </div>
                <div class="stat-row-value revenue-value">
                  {{ formatCurrency(cat.total_revenue) }}
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!loadingCategories && !categoriesError"
          class="empty-stats"
        >
          <feather-icon
            icon="InboxIcon"
            size="40"
            class="text-muted mb-2"
          />
          <p class="text-muted mb-0">
            {{ $t('common.no-data') || 'لا توجد بيانات' }}
          </p>
        </div>

        <!-- Error State -->
        <div
          v-else-if="categoriesError"
          class="empty-stats"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="40"
            class="text-danger mb-2"
          />
          <p class="text-danger mb-0">
            {{ $t('common.error-fetching-data') || 'حدث خطأ أثناء جلب البيانات' }}
          </p>
          <b-button
            variant="outline-danger"
            size="sm"
            class="mt-1"
            @click="fetchCategoryStats"
          >
            {{ $t('common.retry') || 'إعادة المحاولة' }}
          </b-button>
        </div>
      </b-card-text>
    </b-card>

    <!-- ═══════════════════════════════════════════════════════════
         OPERATIONS REPORT TABLE
    ═══════════════════════════════════════════════════════════ -->
    <b-card>
      <b-card-text>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h3 class="mb-0">
            {{ $t('surgery.surgery-operations-report') || 'Surgery Operations Report' }}
          </h3>
        </div>

        <b-table
          :items="summaries"
          :fields="summaryFields"
          :busy="loadingSummaries"
          small
          responsive
          striped
          hover
          show-empty
          :empty-text="$t('common.no-data') || 'لا توجد بيانات'"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner
                small
                class="mr-50"
              />
              <span>{{ $t('common.loading') || 'جاري التحميل...' }}</span>
            </div>
          </template>

          <template #cell(total_revenue)="data">
            <strong class="text-success">{{ formatCurrency(data.item.total_revenue) }}</strong>
          </template>

          <template #cell(supervising_doctors_count)="data">
            <b-badge
              variant="info"
              pill
            >
              {{ (data.item.supervising_doctors || []).length }}
            </b-badge>
          </template>

          <template #cell(total_operations)="data">
            <b-badge
              variant="primary"
              pill
            >
              {{ data.item.total_operations }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="info"
              @click="openDetailsModal(data.item)"
            >
              <feather-icon
                icon="EyeIcon"
                class="mr-25"
              />
              {{ $t('common.details') || 'تفاصيل' }}
            </b-button>
          </template>
        </b-table>

        <div class="d-flex justify-content-between align-items-center mt-2">
          <small v-if="totalSummaries">
            {{ $t('common.total') || 'المجموع' }}: <strong>{{ totalSummaries }}</strong>
          </small>
          <b-pagination
            v-if="totalSummaries > take"
            v-model="page"
            :per-page="take"
            :total-rows="totalSummaries"
            size="sm"
            align="right"
            @input="fetchSurgerySummaries"
          />
        </div>
      </b-card-text>
    </b-card>

    <!-- ═══════════════════════════════════════════════════════════
         DETAILS MODAL
    ═══════════════════════════════════════════════════════════ -->
    <b-modal
      v-model="showDetailsModal"
      size="xl"
      :title="modalTitle"
      hide-footer
      scrollable
    >
      <div
        v-if="loadingDetails"
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
        <p class="mt-2">
          {{ $t('common.loading') || 'جاري التحميل...' }}
        </p>
      </div>

      <div v-else-if="details">
        <b-row class="mb-3">
          <b-col
            md="3"
            cols="6"
            class="mb-2"
          >
            <b-card class="text-center">
              <feather-icon
                icon="ActivityIcon"
                size="24"
                class="text-primary mb-1"
              />
              <h5 class="mb-0">
                {{ details.total_operations }}
              </h5>
              <small class="text-muted">{{ $t('surgery.total-operations') || 'مجموع العمليات' }}</small>
            </b-card>
          </b-col>
          <b-col
            md="3"
            cols="6"
            class="mb-2"
          >
            <b-card class="text-center">
              <feather-icon
                icon="CalendarIcon"
                size="24"
                class="text-info mb-1"
              />
              <h6 class="mb-0">
                {{ formatDate(details.from_date) }}
              </h6>
              <small class="text-muted">{{ $t('common.from-date') || 'من تاريخ' }}</small>
            </b-card>
          </b-col>
          <b-col
            md="3"
            cols="6"
            class="mb-2"
          >
            <b-card class="text-center">
              <feather-icon
                icon="CalendarIcon"
                size="24"
                class="text-info mb-1"
              />
              <h6 class="mb-0">
                {{ formatDate(details.to_date) }}
              </h6>
              <small class="text-muted">{{ $t('common.to-date') || 'الى تاريخ' }}</small>
            </b-card>
          </b-col>
          <b-col
            md="3"
            cols="6"
            class="mb-2"
          >
            <b-card class="text-center">
              <feather-icon
                icon="DollarSignIcon"
                size="24"
                class="text-success mb-1"
              />
              <h6 class="mb-0">
                {{ formatCurrency(calculateTotalRevenue()) }}
              </h6>
              <small class="text-muted">{{ $t('common.total-revenue') || 'إجمالي الإيراد' }}</small>
            </b-card>
          </b-col>
        </b-row>

        <h5 class="mb-2">
          <feather-icon
            icon="ListIcon"
            class="mr-50"
          />
          {{ $t('surgery.operations-list') || 'قائمة العمليات' }}
        </h5>

        <b-table
          :items="details.operations"
          :fields="detailsFields"
          small
          responsive
          striped
          hover
          show-empty
          :tbody-tr-class="rowClass"
          :empty-text="$t('common.no-data') || 'لا توجد بيانات'"
        >
          <template #cell(patient_name)="data">
            <div>
              <strong>{{ data.item.patient_name }}</strong><br>
              <small class="text-muted">{{ $t('reception.mother-name') || 'اسم الأم' }}: {{ data.item.patient_mother_name }}</small>
            </div>
          </template>
          <template #cell(surgery_date_time)="data">
            <feather-icon
              icon="ClockIcon"
              size="14"
              class="mr-25"
            />
            {{ formatDateTime(data.item.surgery_date_time) }}
          </template>
          <template #cell(price)="data">
            {{ formatCurrency(data.item.price) }}
          </template>
          <template #cell(surgery_discount)="data">
            <span :class="data.item.surgery_discount > 0 ? 'text-danger' : ''">
              {{ formatCurrency(data.item.surgery_discount) }}
            </span>
          </template>
          <template #cell(total_price_after_discount)="data">
            <strong class="text-success">{{ formatCurrency(data.item.total_price_after_discount) }}</strong>
          </template>
          <template #cell(invoice_status)="data">
            <b-badge
              :variant="getStatusVariant(data.item.invoice_status)"
              pill
            >
              {{ data.item.invoice_status || 'N/A' }}
            </b-badge>
          </template>
          <template #cell(supervising_doctors)="data">
            <div v-if="data.item.supervising_doctors && data.item.supervising_doctors.length">
              <b-badge
                v-for="(doctor, idx) in data.item.supervising_doctors"
                :key="idx"
                variant="light-primary"
                class="mr-50 mb-25"
              >
                <feather-icon
                  icon="UserIcon"
                  size="12"
                  class="mr-25"
                />
                {{ doctor.doctor_name }}
                <span
                  v-if="doctor.doctor_type"
                  class="text-muted"
                >({{ doctor.doctor_type }})</span>
              </b-badge>
            </div>
            <span
              v-else
              class="text-muted"
            >-</span>
          </template>
          <template #cell(procedure)="data">
            <b-button
              v-if="data.item.procedure"
              v-b-tooltip.hover
              size="sm"
              variant="outline-secondary"
              :title="data.item.procedure"
            >
              <feather-icon
                icon="FileTextIcon"
                size="14"
              />
            </b-button>
            <span
              v-else
              class="text-muted"
            >-</span>
          </template>
          <template #cell(actions)="data">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="navigateToPatientInvoices(data.item)"
            >
              <feather-icon
                icon="FileTextIcon"
                size="14"
                class="mr-25"
              />
              معاينه حسابات المريض
            </b-button>
          </template>
        </b-table>

        <div class="text-right mt-3">
          <b-button
            variant="secondary"
            @click="closeDetailsModal"
          >
            {{ $t('common.close') || 'إغلاق' }}
          </b-button>
        </div>
      </div>

      <div
        v-else-if="detailsError"
        class="text-center py-5"
      >
        <feather-icon
          icon="AlertCircleIcon"
          size="48"
          class="text-danger mb-2"
        />
        <p class="text-danger">
          {{ $t('common.error-fetching-data') || 'حدث خطأ أثناء جلب البيانات' }}
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardText, BRow, BCol, BFormGroup, BButton,
  BTable, BSpinner, BPagination, BModal, BBadge, VBTooltip,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const CATEGORY_COLORS = [
  '#7C3AED', '#0EA5E9', '#10B981', '#F59E0B',
  '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16',
  '#F97316', '#EC4899',
]

export default {
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BSpinner,
    BPagination,
    BModal,
    BBadge,
    Datepicker,
    ToastificationContent,
  },
  directives: { 'b-tooltip': VBTooltip },

  data() {
    return {
      // ── Single shared filter ─────────────────────────
      filters: { from_date: null, to_date: null },
      // Active category filter (from category cards)
      selectedCategoryId: null,

      // ── Category stats ───────────────────────────────
      categoryStats: [],
      loadingCategories: false,
      categoriesError: null,

      // ── Summaries table ──────────────────────────────
      summaries: [],
      loadingSummaries: false,
      summariesError: null,
      page: 1,
      take: 10,
      totalSummaries: 0,

      summaryFields: [
        { key: 'surgery_name', label: this.$t('surgery.surgery-name') || 'اسم العملية', sortable: true },
        { key: 'total_operations', label: this.$t('surgery.total-operations') || 'عدد العمليات', sortable: true },
        { key: 'total_revenue', label: this.$t('common.total-amount') || 'إجمالي الإيراد', sortable: true },
        { key: 'supervising_doctors_count', label: this.$t('surgery.supervising-doctors') || 'عدد الأطباء المشرفين' },
        { key: 'actions', label: this.$t('common.actions') || 'إجراءات' },
      ],

      // ── Modal ────────────────────────────────────────
      showDetailsModal: false,
      details: null,
      loadingDetails: false,
      detailsError: null,
      detailsFields: [
        { key: 'patient_name', label: this.$t('reception.patient-name') || 'اسم المريض' },
        { key: 'surgery_date_time', label: this.$t('surgery.surgery-date') || 'تاريخ العملية' },
        { key: 'price', label: this.$t('common.price') || 'السعر' },
        { key: 'surgery_discount', label: this.$t('common.discount') || 'الخصم' },
        { key: 'total_price_after_discount', label: this.$t('surgery.total-after-discount') || 'الإجمالي بعد الخصم' },
        { key: 'invoice_status', label: this.$t('common.status') || 'حالة الفاتورة' },
        { key: 'supervising_doctors', label: this.$t('surgery.supervising-doctors') || 'الأطباء المشرفون' },
        { key: 'procedure', label: this.$t('surgery.procedure') || 'الإجراء' },
        { key: 'actions', label: this.$t('common.actions') || 'إجراءات' },
      ],
    }
  },

  computed: {
    isLoading() {
      return this.loadingCategories || this.loadingSummaries
    },
    grandTotalOperations() {
      return this.categoryStats.reduce((s, c) => s + (c.total_operations || 0), 0)
    },
    grandTotalRevenue() {
      return this.categoryStats.reduce((s, c) => s + (Number(c.total_revenue) || 0), 0)
    },
    grandTotalCollected() {
      return this.categoryStats.reduce((s, c) => s + (Number(c.total_money_collected) || 0), 0)
    },
    activeDateLabel() {
      const from = this.filters.from_date ? this.formatDate(this.filters.from_date) : null
      const to = this.filters.to_date ? this.formatDate(this.filters.to_date) : null
      if (from && to) return `${from}  →  ${to}`
      if (from) return `${this.$t('common.from-date') || 'من'}: ${from}`
      if (to) return `${this.$t('common.to-date') || 'إلى'}: ${to}`
      return null
    },
    modalTitle() {
      return this.details
        ? `${this.details.surgery_name} - ${this.$t('common.details') || 'تفاصيل'}`
        : this.$t('common.details') || 'تفاصيل'
    },
  },

  created() {
    this.fetchAll()
  },

  methods: {
    // ── Shared filter actions ──────────────────────────
    applyFilters() {
      this.page = 1
      this.fetchAll()
    },
    resetFilters() {
      this.filters.from_date = null
      this.filters.to_date = null
      this.page = 1
      this.fetchAll()
    },
    /** Fire both requests simultaneously */
    fetchAll() {
      this.fetchCategoryStats()
      this.fetchSurgerySummaries()
    },

    // ── Shared date query builder ──────────────────────
    buildDateParams() {
      const params = []
      if (this.filters.from_date) params.push(`from=${this.formatDate(this.filters.from_date)}`)
      if (this.filters.to_date) params.push(`to=${this.formatDate(this.filters.to_date)}`)
      return params.length ? `?${params.join('&')}` : ''
    },

    // ── Helpers ────────────────────────────────────────
    getCategoryColor(index) {
      return CATEGORY_COLORS[index % CATEGORY_COLORS.length]
    },
    getRevenueShare(revenue) {
      if (!this.grandTotalRevenue) return 0
      return Math.round((Number(revenue) / this.grandTotalRevenue) * 100)
    },
    getCollectionRate(cat) {
      const ops = cat.total_operations || 0
      const collected = Number(cat.total_money_collected) || 0
      if (!ops) return 0
      return Math.min(100, Math.round((collected / ops) * 100))
    },
    getCollectionRateColor(rate) {
      if (rate >= 80) return '#10B981'
      if (rate >= 50) return '#F59E0B'
      return '#EF4444'
    },
    handleCategoryClick(cat) {
      if (!cat || !cat.id) return
      // Toggle selection: clicking the same card again clears the filter
      this.selectedCategoryId = this.selectedCategoryId === cat.id ? null : cat.id
      this.page = 1
      this.fetchSurgerySummaries()
    },
    formatDate(date) {
      if (!date || date === 'all') return this.$t('common.all') || 'الكل'
      try {
        const d = new Date(date)
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      } catch (e) { return date }
    },
    formatDateTime(value) {
      if (!value) return '-'
      try {
        const d = new Date(value)
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
      } catch (e) { return value }
    },
    formatCurrency(amount) {
      if (amount == null || amount === '') return '-'
      const num = Number(amount)
      if (Number.isNaN(num)) return amount
      try {
        return new Intl.NumberFormat('en-IQ', { style: 'currency', currency: 'IQD', maximumFractionDigits: 0 }).format(num)
      } catch (e) { return num.toLocaleString() }
    },

    // ── Category Stats ─────────────────────────────────
    async fetchCategoryStats() {
      try {
        this.loadingCategories = true
        this.categoriesError = null
        await this.getList(`reports/surgery-categories${this.buildDateParams()}`)
        const { data } = this.all_data
        this.categoryStats = Array.isArray(data.categories) ? data.categories : []
      } catch (error) {
        this.categoriesError = error
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: this.$t('common.error') || 'خطأ', variant: 'danger', text: error?.response?.data?.message || this.$t('common.error-fetching-data') || 'حدث خطأ' },
        })
      } finally {
        this.loadingCategories = false
      }
    },

    // ── Summaries Table ────────────────────────────────
    async fetchSurgerySummaries() {
      try {
        this.loadingSummaries = true
        this.summariesError = null
        const dateStr = this.buildDateParams()
        // If dateStr is '?from=...', append page/take with '&'; otherwise start with '?'
        const pagePart = `page=${this.page}&take=${this.take}`
        const categoryPart = this.selectedCategoryId ? `&surgery_category_id=${this.selectedCategoryId}` : ''
        const url = dateStr
          ? `reports/surgery-operations${dateStr}&${pagePart}${categoryPart}`
          : `reports/surgery-operations?${pagePart}${categoryPart}`
        await this.getList(url)
        const { data } = this.all_data
        this.summaries = Array.isArray(data.surgeries) ? data.surgeries : []
        this.totalSummaries = data.total || this.summaries.length
      } catch (error) {
        this.summariesError = error
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: this.$t('common.error') || 'خطأ', variant: 'danger', text: error?.response?.data?.message || this.$t('common.error-fetching-data') || 'حدث خطأ' },
        })
      } finally {
        this.loadingSummaries = false
      }
    },

    // ── Modal ──────────────────────────────────────────
    async openDetailsModal(summary) {
      if (!summary || !summary.surgery_id) return
      this.showDetailsModal = true
      await this.fetchSurgeryDetails(summary.surgery_id)
    },
    closeDetailsModal() {
      this.showDetailsModal = false
      this.details = null
      this.detailsError = null
    },
    async fetchSurgeryDetails(surgeryId) {
      try {
        this.loadingDetails = true
        this.detailsError = null
        this.details = null
        await this.getList(`reports/surgery-operations/${surgeryId}`)
        const { data } = this.all_data
        if (data) {
          this.details = {
            surgery_id: data.surgery_id,
            surgery_name: data.surgery_name,
            from_date: data.from_date,
            to_date: data.to_date,
            total_operations: data.total_operations,
            operations: Array.isArray(data.operations) ? data.operations : [],
          }
        }
      } catch (error) {
        this.detailsError = error
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: this.$t('common.error') || 'خطأ', variant: 'danger', text: error?.response?.data?.message || this.$t('common.error-fetching-data') || 'حدث خطأ' },
        })
      } finally {
        this.loadingDetails = false
      }
    },
    calculateTotalRevenue() {
      if (!this.details || !this.details.operations) return 0
      return this.details.operations.reduce((sum, op) => sum + (op.total_price_after_discount || 0), 0)
    },
    getStatusVariant(status) {
      const s = (status || '').toLowerCase()
      if (s === 'pending') return 'warning'
      if (s === 'paid' || s === 'completed') return 'success'
      if (s === 'cancelled') return 'danger'
      return 'secondary'
    },
    isInvoicePending(operation) {
      return (operation.invoice_status || '').toLowerCase() === 'pending'
    },
    rowClass(item) {
      return item && this.isInvoicePending(item) ? 'table-warning' : ''
    },
    navigateToPatientInvoices(item) {
      const { patient_id: patientId, patient_card_id: patientCardId } = item
      if (!patientId || !patientCardId) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: this.$t('common.error') || 'خطأ', text: this.$t('common.error-fetching-data') || 'حدث خطأ', variant: 'warning' },
        })
        return
      }
      this.$router.push({
        name: 'acounting-invoices',
        params: {
          patientId, patientCardId, isPatientAccounting: 'false', isSurgeryAccounting: 'true',
        },
      })
    },
  },
}
</script>

<style scoped>
/* ── Filter Bar ─────────────────────────────────────── */
.filter-bar-card {
  border: 1.5px solid #e2e8f8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.06);
}

.filter-bar-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2d3748;
}

.active-filter-pill {
  display: inline-flex;
  align-items: center;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
}

/* ── Stats Card ─────────────────────────────────────── */
.stats-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
}

.stats-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7c3aed22, #0ea5e922);
  display: flex;
  align-items: center;
  justify-content: center;
}

.stats-icon { color: #7c3aed; }
.stats-title { font-size: 1.05rem; font-weight: 700; color: #2d3748; }

.refresh-btn {
  border-radius: 8px;
  width: 34px;
  height: 34px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes spin { to { transform: rotate(360deg); } }
.spin-icon { animation: spin 0.8s linear infinite; }

/* ── Totals Strip ───────────────────────────────────── */
.totals-strip {
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
  border: 1px solid #e2e8f8;
  border-radius: 10px;
  padding: 0.75rem 1.25rem;
}

.totals-strip-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.total-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #4a5568;
  flex: 1;
  min-width: 150px;
}

.total-label { color: #718096; }
.total-value { font-weight: 700; font-size: 0.9rem; }
.total-divider { width: 1px; height: 24px; background: #cbd5e0; flex-shrink: 0; }

/* ── Category Cards ─────────────────────────────────── */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.category-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8edf5;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.category-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.category-card--active {
  border-color: #7c3aed;
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.3);
}

.card-top-bar { height: 4px; background: var(--card-accent, #7c3aed); }

.category-card-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1rem 0.5rem;
}

.category-rank {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--card-accent, #7c3aed);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.category-name-wrap { flex: 1; min-width: 0; }

.category-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-share-badge {
  background: var(--card-accent, #7c3aed);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  flex-shrink: 0;
}

.revenue-progress-wrap { padding: 0 1rem 0.5rem; }

.revenue-progress-track {
  height: 4px;
  background: #edf2f7;
  border-radius: 99px;
  overflow: hidden;
}

.revenue-progress-fill {
  height: 100%;
  background: var(--card-accent, #7c3aed);
  border-radius: 99px;
  transition: width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.category-stats { padding: 0 1rem 1rem; }

.stat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0;
  border-bottom: 1px dashed #f0f4f8;
  font-size: 0.82rem;
}

.stat-row:last-of-type { border-bottom: none; }
.stat-row-label { display: flex; align-items: center; color: #718096; flex: 1; }
.stat-row-value { font-weight: 600; color: #2d3748; }
.revenue-value { color: #10b981; font-size: 0.8rem; }
.stat-badge { font-size: 0.75rem; padding: 3px 8px; }

.collection-rate-wrap { margin-top: 0.5rem; }
.collection-rate-label { display: flex; justify-content: space-between; margin-bottom: 4px; }
.collection-pct { font-weight: 700; color: #4a5568; }
.collection-track { height: 5px; background: #edf2f7; border-radius: 99px; overflow: hidden; }
.collection-fill { height: 100%; border-radius: 99px; transition: width 0.7s cubic-bezier(0.34, 1.56, 0.64, 1); }

/* ── Skeleton ────────────────────────────────────────── */
.stats-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.skeleton-card {
  background: #f7f8fc;
  border-radius: 12px;
  border: 1px solid #e8edf5;
  padding: 1rem;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-header { height: 18px; background: #e2e8f0; border-radius: 6px; margin-bottom: 0.75rem; width: 60%; }
.skeleton-bar { height: 12px; background: #e2e8f0; border-radius: 6px; margin-bottom: 0.5rem; }

@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

/* ── Empty / Error ───────────────────────────────────── */
.empty-stats {
  text-align: center;
  padding: 3rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Misc ────────────────────────────────────────────── */
.table-warning { background-color: #fff3cd !important; }
.mr-25 { margin-right: 0.25rem; }
.mr-50 { margin-right: 0.5rem; }
.mb-25 { margin-bottom: 0.25rem; }

@media (max-width: 576px) {
  .totals-strip-inner { gap: 0.75rem; }
  .total-divider { display: none; }
  .total-item { min-width: 120px; }
}
</style>
